'use client'; // NextJS needs this to be a client comp

import { useEffect } from 'react';

import { faker } from '@faker-js/faker';
import * as Sentry from '@sentry/nextjs';
import Link from 'next/link';

import { CodeSnippet } from '@/components/CodeSnippet';
import { PageSection } from '@/components/PageSection';
import { SectionHeading } from '@/components/SectionHeading';
import { Wrapper } from '@/components/Wrapper';
import { useFlagID } from '@/hooks/useFlags';

interface IProps {
  error: Error;
}

export default function ErrorPage(props: IProps) {
  const { error } = props;
  const flagID = useFlagID();
  const numericHash = parseInt(flagID.substring(0, 8), 16);
  faker.seed(numericHash); // this should give a repeatable value for a given seed
  const code = [faker.animal.petName(), faker.book.title()]
    .join('-')
    .replaceAll(' ', '-')
    .toLocaleLowerCase();

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'gobal-error',
      message: error.message,
      data: {
        id: flagID,
        code,
      },
    });

    Sentry.captureException(error);
  }, [error, flagID, code]);

  return (
    <PageSection>
      <Wrapper>
        <SectionHeading
          title="Error - Something happened on our end"
          subtitle="Sorry about that, we've been notified and will get it patched up ASAP."
        />
        <p className="mb-1">
          If you&apos;re looking for something specific,{' '}
          <Link href="/search" className="text-link">
            you can search for it here
          </Link>
          .
        </p>
        <p>
          Know what you&apos;re looking for & want us to cover it?{' '}
          <Link href="/contact" className="text-link">
            Reach out to us
          </Link>{' '}
          to suggest a topic.
        </p>
        <ErrorDetails
          className="mt-8"
          error={error}
          hashCode={code}
          flagID={flagID}
        />
      </Wrapper>
    </PageSection>
  );
}

interface IErrorDetailsProps {
  error: Error;
  hashCode: string;
  flagID: string;
  className?: string;
}

function ErrorDetails({
  error,
  hashCode,
  flagID,
  className,
}: IErrorDetailsProps) {
  return (
    <div className={className}>
      <CodeSnippet className="bg-gray-100 p-3 md:p-5 rounded-2 max-h-[30rem] overflow-auto text-body-300 leading-tight">
        {hashCode + ' ' + flagID + '\n'}
        {error.name + '\n'}
        {error.stack}
      </CodeSnippet>
    </div>
  );
}
